// @KindParams — shared param-builders for scenery-kind defs (the scenery analog of the material
// system's `RecipeUtils.ts`). Each `Kind_*.ts` file uses these to declare its params; they live
// here so a second kind (rock, bush, …) doesn't duplicate the swing/color wiring.
//
// A scalar param becomes a `ParamSchemaWithSpread`: the base `ParamSchema` (value default +
// min/max + label) plus its optional swing/curve defaults. The swing fields live on the
// INTERSECTION type, NOT on `ParamSchema` (the material system has no variants and would carry a
// permanently-ignored field), so they are attached after the base schema is constructed. Each
// param is declared EXACTLY ONCE (in its kind's `params` array) — the §4.3 b double-entry
// regression guard.
import {ParamSchemaWithSpread} from "../@SceneryTypes.js";
import {ParamSchema} from "../../Materials/@MaterialTypes.js";

/** A scalar param. ALL data — base `ParamSchema` fields (key/label/min/max/default) + optional
 *  `swingDefault`/`curveDefault` — goes in the single `baseData` object. `swingDefault` is a
 *  HALF-WIDTH (`default ± swing`, intersected with [min,max]); "log" curve applies it ×/÷. */
export interface KindParamData extends Partial<ParamSchema> {
	key: string;
	kind: "scalar";
	swingDefault?: number;
	curveDefault?: "linear" | "log";
}

export function kindParam(data: KindParamData): ParamSchemaWithSpread {
	const {swingDefault, curveDefault, ...schemaData} = data;
	const schema = new ParamSchema(schemaData) as ParamSchemaWithSpread;
	if (swingDefault != null) schema.swingDefault = swingDefault;
	if (curveDefault != null) schema.curveDefault = curveDefault;
	return schema;
}

/** An INT param (Set14 P2 §4.3 a) — an integer stratum, ROUNDED after sampling (the value is an
 *  integer in every variant; the swing is in the same integer units). Mirrors `kindParam`'s
 *  swing/curve attachment but on the `int` kind. */
export function kindIntParam(data: Partial<ParamSchema> & {key: string; group?: string; swingDefault?: number}): ParamSchemaWithSpread {
	const {swingDefault, ...schemaData} = data;
	const schema = new ParamSchema({...schemaData, kind: "int"}) as ParamSchemaWithSpread;
	if (swingDefault != null) schema.swingDefault = swingDefault;
	return schema;
}

/** An ENUM param (Set14 P2 §4.3 a) — a discrete string carrier, NEVER swung (§4.13: the editor
 *  refuses to swing enum/bool/vec3 and `deriveVariants` drops any swing by kind). */
export function kindEnumParam(data: Partial<ParamSchema> & {key: string; group?: string; default: string; options: {value: string; label: string}[]}): ParamSchemaWithSpread {
	return new ParamSchema({...data, kind: "enum"}) as ParamSchemaWithSpread;
}

/** A BOOL param (Set14 P2 §4.3 a) — a boolean carrier, NEVER swung (§4.13). */
export function kindBoolParam(data: Partial<ParamSchema> & {key: string; group?: string; default: boolean}): ParamSchemaWithSpread {
	return new ParamSchema({...data, kind: "bool"}) as ParamSchemaWithSpread;
}

/** A colour param (0..1 RGB) — NEVER swung (a colour can't be LHS-sampled usefully; per-kind
 *  `crownColorVariation`-style params + the instance `tint` jitter handle the visible spread). */
export function kindColorParam(baseData: Partial<ParamSchema> & {key: string; kind: "color"}): ParamSchemaWithSpread {
	return new ParamSchema({...baseData, min: baseData.min ?? 0, max: baseData.max ?? 1}) as ParamSchemaWithSpread;
}
